/**
 * Ultra-Modern Layout Component
 * World-Class SaaS ERP Platform Design
 */

import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { SupportAgentWidget } from '../shared/SupportAgentWidget';
import {
  initWatermark,
  destroyWatermark,
  applyBlurProtection,
  applyScreenShareBlock,
} from '@euroasiann/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function TemplateLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const hideFloatingChat = location.pathname.includes('/support/chat');

  useEffect(() => {
    if (!user) return;

    const policy = user.contentProtection ?? {
      screenshotAllowed: true,
      screenRecordingAllowed: true,
      screenShareAllowed: true,
    };

    if (!policy.screenshotAllowed) {
      initWatermark({
        userName: `${user.firstName} ${user.lastName}`,
        userRole: user.role,
        ip: user.ip ?? 'unknown',
      });
    }

    applyBlurProtection(!policy.screenRecordingAllowed);

    applyScreenShareBlock(!policy.screenShareAllowed, () => {
      fetch(`${API_URL}/api/v1/tech/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          action: 'screen_share_blocked',
          userId: user._id,
          portal: 'admin',
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    });

    return () => {
      destroyWatermark();
      applyBlurProtection(false);
      applyScreenShareBlock(false);
    };
  }, [user]);

  return (
    <div className="flex h-screen w-full bg-[hsl(var(--background))] text-[hsl(var(--foreground))] overflow-hidden relative app-scaling">
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={(collapsed) => setSidebarCollapsed(collapsed)} 
      />
      <div
        className={cn(
          'flex-1 min-w-0 flex flex-col h-full transition-all duration-300 ease-in-out bg-[hsl(var(--background))]',
          sidebarCollapsed ? 'ml-20' : 'ml-72'
        )}
      >
        <Header
          sidebarCollapsed={sidebarCollapsed}
          onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--background))] pt-16">
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
      {!hideFloatingChat && <SupportAgentWidget />}
    </div>
  );
}






